import { useMemo } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { useJournalDetailsBreadcrumb } from 'pages/app/staff/journal/journalDetails/hooks';
import { GroupConsultationId } from 'types';
import { appPaths } from 'consts/paths/paths';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { Details } from 'components/organisms';
import { parseStringIntoFormattedStringDate } from 'utils/parsers/dateTime/parseStringIntoFormattedStringDate';
import { groupConsultationDTOFields } from 'consts/consultation/groupConsultation/groupConsultationDTOFields';
import { PersonList } from 'components/molecules';
import { commonTexts, journalTexts } from 'consts/text';
import { useJournalAbilities } from 'abilities';
import { getUniqueParentsFromChildrenWithParents } from 'utils/getUniqueParentsFromChildrenWithParents';

export const GroupJournalConsultationDetailsPage = () => {
    const { journalId } = useJournalId();
    const { consultationId: consultationIdParam } = useParams();
    const {
        groupJournalsAbilities: { checkIfCanManageConsultations },
    } = useJournalAbilities();

    useJournalDetailsBreadcrumb({
        journalId,
        journalType: 'GROUP',
    });

    const canManageConsultations = checkIfCanManageConsultations();
    const consultationId = consultationIdParam as GroupConsultationId;
    const editPagePath = generatePath(
        appPaths.app.journals.groupJournalDetails.pickedJournal.consultations.edit
            .pickedConsultation,
        { consultationId, journalId },
    );

    const {
        data: consultationData,
        isFetching: isConsultationDataFetching,
        isError: isFetchConsultationDataError,
    } = useAppQuery('CONSULTATION', [journalId, consultationId], () =>
        StaffHTTPService.groupConsultations.getGroupConsultation(journalId, consultationId),
    );

    const parentsData = useMemo(() => {
        if (!consultationData) {
            return [];
        }
        return getUniqueParentsFromChildrenWithParents(
            consultationData[groupConsultationDTOFields.childrenWithParents],
        );
    }, [consultationData]);

    return (
        <Details
            title={journalTexts.journalDetails.common.consultations.consultationDetails}
            isLoading={isConsultationDataFetching}
            isError={isFetchConsultationDataError}
            editPagePath={canManageConsultations ? editPagePath : undefined}
        >
            <Details.Item
                title={journalTexts.journalDetails.common.consultations.consultationSubject}
            >
                {consultationData?.[groupConsultationDTOFields.subject]}
            </Details.Item>
            <Details.Item title={commonTexts.dataLabels.date}>
                {consultationData &&
                    parseStringIntoFormattedStringDate(
                        consultationData[groupConsultationDTOFields.date],
                    )}
            </Details.Item>
            <Details.Item
                title={journalTexts.journalDetails.common.consultations.consultationDescription}
            >
                {consultationData?.[groupConsultationDTOFields.description]}
            </Details.Item>
            <Details.Item title={commonTexts.dataLabels.parentList}>
                <PersonList personsData={parentsData} />
            </Details.Item>
        </Details>
    );
};
